import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';

import { BaseChartDirective } from 'ng2-charts';
import { Rule } from '../../../models/rule.model';
import { Store } from '@ngrx/store';
import { loadHealthIndicatorTrendIfNeeded } from '../../../store/actions/rule-result.actions';
import { selectHealthIndicatorTrendData } from '../../../store/selectors/rule-result.selector';
import { TIME_FRAME } from '../../../shared/constants/filters.constants';
import { Subject, takeUntil, filter } from 'rxjs';
import { DefaultChartOptions } from '../../../../utils/chart/default-options.utils';
import { ThemeService } from '../../../core/services/theme.service';
import {
  isActivationConditionStale,
  isRuleStale,
} from '../../../../utils/rule.utils';
import { mapErrorMessage } from '../../../../utils/error.utils';

@Component({
  selector: 'app-health-indicator-chart',
  templateUrl: './health-indicator-chart.component.html',
  styleUrl: './health-indicator-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HealthIndicatorChartComponent
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
  @Input() rule: Rule | null = null;
  private destroy$ = new Subject<void>();
  public isLoading: boolean = true;
  constructor(
    private store: Store,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private themeService: ThemeService,
  ) {}

  public chartPlugins: any[] = [DefaultChartOptions.getVerticalLinePlugin()];

  animatedEvaluationsPassingMinMaxAlertThresholdRatio = 0;
  animatedEvaluationsPassingMinMaxToleranceThresholdRatio = 0;

  public lineChartData: Array<{ x: number; y: number }> = [];
  public lineChartLabels: string[] = [];
  public lineChartOptions: any;

  get isActivationConditionStale(): boolean {
    return isActivationConditionStale(this.rule);
  }

  get isRuleStale(): boolean {
    return isRuleStale(this.rule);
  }

  get errorMessage(): string {
    return mapErrorMessage(this.rule?.lastErrorType ?? '');
  }

  public get chartDatasets(): any[] {
    const datasets: any[] = [
      DefaultChartOptions.getHealthIndicatorDataset(
        this.lineChartData,
        this.rule?.periodInMinutes ?? 0,
      ),
    ];

    if (this.lineChartData.length > 0) {
      const now = Date.now();
      const xStart = now - 30 * 24 * 60 * 60 * 1000;
      const xEnd = now;

      if (this.rule?.alertThreshold != null) {
        datasets.push(
          this.buildThresholdDataset(
            "Seuil d'alarme",
            xStart,
            xEnd,
            this.rule.alertThreshold,
            '#ef4444',
          ),
        );
      }
      if (this.rule?.minAlertThreshold != null) {
        datasets.push(
          this.buildThresholdDataset(
            "Seuil d'alarme minimal",
            xStart,
            xEnd,
            this.rule.minAlertThreshold,
            '#ef4444',
          ),
        );
      }
      if (this.rule?.toleranceThreshold != null) {
        datasets.push(
          this.buildThresholdDataset(
            'Seuil de tolérance',
            xStart,
            xEnd,
            this.rule.toleranceThreshold,
            '#f59e0b',
          ),
        );
      }
      if (this.rule?.minToleranceThreshold != null) {
        datasets.push(
          this.buildThresholdDataset(
            'Seuil de tolérance minimal',
            xStart,
            xEnd,
            this.rule.minToleranceThreshold,
            '#f59e0b',
          ),
        );
      }
    }

    return datasets;
  }

  private buildThresholdDataset(
    label: string,
    xStart: number,
    xEnd: number,
    value: number,
    color: string,
  ): any {
    return {
      label,
      data: [
        { x: xStart, y: value },
        { x: xEnd, y: value },
      ],
      borderColor: color,
      borderWidth: 1.5,
      borderDash: [6, 4],
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
      spanGaps: true,
      tension: 0,
      parsing: false,
    };
  }

  ngOnInit() {
    this.lineChartOptions = DefaultChartOptions.getHealthIndicatorOptions();
    this.themeService.darkMode$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.lineChartOptions = DefaultChartOptions.getHealthIndicatorOptions();
      DefaultChartOptions.updateHealthIndicatorDatasetColors(this.chart?.chart);
      this.chart?.update();
    });
  }

  private loadRuleData() {
    if (!this.rule?.uid) return;

    this.isLoading = true;
    setTimeout(() => {
      if (!this.rule?.uid) return;
      const interpolationInMinutes = Math.max(
        this.rule.piPointInterpolationIntervalInMinutes ?? 60,
        60,
      );
      this.store.dispatch(
        loadHealthIndicatorTrendIfNeeded({
          uid: this.rule.uid,
          range: TIME_FRAME.MONTH,
          alertThreshold: this.rule.alertThreshold ?? 0,
          interpolationInMinutes,
        }),
      );

      this.store
        .select(selectHealthIndicatorTrendData(this.rule.uid, TIME_FRAME.MONTH))
        .pipe(
          filter((data) => !!data),
          takeUntil(this.destroy$),
        )
        .subscribe((data) => {
          this.lineChartData = data.map((d) => ({
            x: new Date(d.ts).getTime(),
            y: d.value ?? 0,
          }));
          this.lineChartLabels = data.map((d) =>
            new Date(d.ts).toLocaleString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }),
          );
          this.isLoading = false;
          this.cdr.detectChanges();
        });
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rule'] && this.rule) {
      setTimeout(() => {
        this.animatedEvaluationsPassingMinMaxAlertThresholdRatio = parseFloat(
          (
            (Math.max(
              this.rule?.evaluationsAboveAlertThresholdRatio ?? 0,
              this.rule?.evaluationsBelowMinAlertThresholdRatio ?? 0,
            ) ?? 0) * 100
          ).toFixed(2),
        );
        this.animatedEvaluationsPassingMinMaxToleranceThresholdRatio =
          parseFloat(
            (
              (Math.max(
                this.rule?.evaluationsAboveToleranceThresholdRatio ?? 0,
                this.rule?.evaluationsBelowMinToleranceThresholdRatio ?? 0,
              ) ?? 0) * 100
            ).toFixed(2),
          );
        this.cdr.detectChanges();
      }, 100);
      this.loadRuleData();
    }
  }

  onChartClick(): void {
    if (this.rule?.uid) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['factory/rule'], {
          queryParams: { id: this.rule.uid },
        }),
      );
      window.open(url, '_blank');
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
